import { awaitJSON, DigitalAuditLambdaFetch } from '@src/utils/fetch-utils';
import { FetchError } from '@src/utils/fetch-error';
import {
  CreateDomainConfigInput,
  CreateDomainConfigResult,
  DomainConfigChange,
  ListDomainConfigChangesResult,
  ResolveDomainConfigResult,
  UpdateDomainConfigInput,
  UpdateDomainConfigResult
} from './domain-config-constants';

/**
 * Resolves the canonical domain-config row for a URL, if this tool has ever
 * seen that domain before. A brand-new domain is the common, expected case -
 * the backend responds 404 for it, not an error - so that's caught here and
 * normalized to `null` rather than left to throw.
 */
export const resolveDomainConfig = async (
  url: string
): Promise<ResolveDomainConfigResult | null> => {
  try {
    const res = await DigitalAuditLambdaFetch(
      `/digital-audit/domain-config/resolve?url=${encodeURIComponent(url)}`,
      { method: 'GET' }
    );
    return await awaitJSON<ResolveDomainConfigResult>(res);
  } catch (e) {
    if (e instanceof FetchError && e.status === 404) {
      return null;
    }
    throw e;
  }
};

/**
 * Creates (or, idempotently, fetches) the canonical domain-config row for a
 * URL. Safe to call even if the domain already exists - the backend returns
 * `created: false` and the existing values in that case.
 */
export const createDomainConfig = async (
  input: CreateDomainConfigInput
): Promise<CreateDomainConfigResult> => {
  const res = await DigitalAuditLambdaFetch('/digital-audit/domain-config', {
    method: 'POST',
    body: input
  });
  return awaitJSON<CreateDomainConfigResult>(res);
};

/**
 * The only path that changes a domain's canonical client_type/vertical - the
 * backend audit-logs each changed field. `expected_updated_at` is the
 * `updated_at` last read; a stale one fails with a 409 FetchError, in which
 * case the caller should refetch and retry.
 */
export const updateDomainConfig = async (
  domain: string,
  input: UpdateDomainConfigInput
): Promise<UpdateDomainConfigResult> => {
  const res = await DigitalAuditLambdaFetch(
    `/digital-audit/domain-config/${encodeURIComponent(domain)}`,
    { method: 'PATCH', body: input }
  );
  return awaitJSON<UpdateDomainConfigResult>(res);
};

export const listDomainConfigChanges = async (domain: string): Promise<DomainConfigChange[]> => {
  const res = await DigitalAuditLambdaFetch(
    `/digital-audit/domain-config/${encodeURIComponent(domain)}/changes`,
    { method: 'GET' }
  );
  const { changes } = await awaitJSON<ListDomainConfigChangesResult>(res);
  return changes;
};
